import React, { useState, useEffect, useRef } from 'react';
import './Stats.css';

const stats = [
  {
    value: 6500, suffix: '+',
    label: 'Vehicles Tracked',
    sub: 'Active fleet across Pakistan',
    dataNum: '2K',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#D01B1B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="11" width="10" height="7" rx="1.5"/>
        <circle cx="4.5" cy="19.5" r="1.5"/><circle cx="8.5" cy="19.5" r="1.5"/>
        <path d="M11 14 L15 11 L21 11 L21 18 L16 18 L11 18"/>
        <circle cx="17" cy="19.5" r="1.5"/>
      </svg>
    ),
  },
  {
    value: 99.9, suffix: '%',
    label: 'Uptime Guaranteed',
    sub: 'Server reliability SLA',
    dataNum: '99',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#D01B1B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 L14.5 8.5 L21.5 8.5 L16 12.5 L18.5 19 L12 15 L5.5 19 L8 12.5 L2.5 8.5 L9.5 8.5 Z"/>
      </svg>
    ),
  },
  {
    value: 24, suffix: '/7',
    label: 'Customer Support',
    sub: 'Avg. 4 min response time',
    dataNum: '24',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#D01B1B" strokeWidth="1.6" strokeLinecap="round">
        <circle cx="12" cy="12" r="9"/>
        <path d="M12 7 L12 12 L15.5 14.5"/>
      </svg>
    ),
  },
  {
    value: 7, suffix: '+',
    label: 'Years of Excellence',
    sub: 'Est. 2019 in Karachi',
    dataNum: '7',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="#D01B1B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 L14 8 L20.5 8 L15 11.5 L17 18 L12 14 L7 18 L9 11.5 L3.5 8 L10 8 Z"/>
        <path d="M8 21 L12 19 L16 21"/>
      </svg>
    ),
  },
];

function Counter({ value, suffix }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && !started) setStarted(true); },
      { threshold: 0.4 }
    );
    if (ref.current) ob.observe(ref.current);
    return () => ob.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const dec = value % 1 !== 0;
    const steps = 72, dur = 2000, st = dur / steps;
    let i = 0;
    const timer = setInterval(() => {
      i++;
      const ease = 1 - Math.pow(1 - i / steps, 3);
      setCount(dec ? parseFloat((ease * value).toFixed(1)) : Math.round(ease * value));
      if (i >= steps) clearInterval(timer);
    }, st);
    return () => clearInterval(timer);
  }, [started, value]);

  return <span ref={ref} className="counter-val">{count}{suffix}</span>;
}

const Stats = () => {
  const cardRefs = useRef([]);

  // Reveal on scroll
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('visible');
      });
    }, { threshold: 0.2 });
    cardRefs.current.forEach(c => c && obs.observe(c));
    return () => obs.disconnect();
  }, []);

  // 3D tilt on hover
  const handleMouseMove = (e, el) => {
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transition = 'transform 0.1s ease, border-color 0.3s, box-shadow 0.3s';
    el.style.transform = `perspective(600px) rotateY(${x * 10}deg) rotateX(${-y * 10}deg) translateZ(8px)`;
  };

  const handleMouseLeave = (el) => {
    el.style.transition = 'transform 0.5s ease, border-color 0.3s, box-shadow 0.3s';
    el.style.transform = 'perspective(600px) rotateY(0deg) rotateX(0deg) translateZ(0)';
  };

  return (
    <section className="stats-section">
      <div className="container">
        <div className="stats-header">
          <p className="stats-eyebrow">Trusted by Pakistan's leading businesses</p>
        </div>
        <div className="stats-grid">
          {stats.map((s, i) => (
            <div
              key={i}
              className="stat-card reveal"
              ref={el => cardRefs.current[i] = el}
              style={{ transitionDelay: `${i * 80}ms` }}
              onMouseMove={(e) => handleMouseMove(e, cardRefs.current[i])}
              onMouseLeave={() => handleMouseLeave(cardRefs.current[i])}
            >
              <div className="stat-num-ghost">{s.dataNum}</div>
              <div className="stat-top">
                <div className="stat-icon">{s.icon}</div>
              </div>
              <Counter value={s.value} suffix={s.suffix} />
              <div className="stat-label">{s.label}</div>
              <div className="stat-sub">{s.sub}</div>
              <div className="stat-bar-line" />
              <div className="stat-shine" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;